import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Check, ArrowRight } from 'lucide-react';
import useBoardStore from '../../../store/boardStore';
import useAuthStore from '../../../store/authStore';
import useUserSearch from '../../../hooks/useUserSearch';
import { GRANT_LEVELS, DEFAULT_GRANT_LEVEL } from '../../../constants/access';
import { WORKSPACE_ICONS, WORKSPACE_ICON_NAMES, defaultColorForWorkspace } from './workspaceIcons';

function Avatar({ user, size = 7 }) {
  if (user?.avatar_url) {
    return <img src={user.avatar_url} className={`w-${size} h-${size} rounded-full object-cover shrink-0`} alt={user.name} />;
  }
  return (
    <div className={`w-${size} h-${size} rounded-full bg-accent flex items-center justify-center text-white text-xs font-bold shrink-0`}>
      {user?.name?.[0]?.toUpperCase() ?? '?'}
    </div>
  );
}

function Preview({ name, icon, size = 56 }) {
  const color = defaultColorForWorkspace(0);
  const IconComp = icon ? WORKSPACE_ICONS[icon] : null;
  if (IconComp) {
    return (
      <div className="rounded-lg flex items-center justify-center" style={{ width: size, height: size, backgroundColor: color }}>
        <IconComp className="text-white" style={{ width: size * 0.55, height: size * 0.55 }} />
      </div>
    );
  }
  const letter = (name || '?').trim().charAt(0).toUpperCase();
  return (
    <div
      className="rounded-lg flex items-center justify-center text-white font-semibold"
      style={{ width: size, height: size, backgroundColor: color, fontSize: size * 0.45 }}
    >
      {letter}
    </div>
  );
}

// Dialogo completo de criacao de area de trabalho: nome, icone, pessoas com
// nivel de acesso e grupos (roles) que enxergam a area — tudo enviado num
// unico POST /workspaces { name, icon, members, role_ids }.
export default function WorkspaceCreateModal({ onClose, onCreated }) {
  const user = useAuthStore((s) => s.user);
  const createWorkspace = useBoardStore((s) => s.createWorkspace);
  const roles = useBoardStore((s) => s.roles);
  const loadingRoles = useBoardStore((s) => s.loadingRoles);
  const fetchRoles = useBoardStore((s) => s.fetchRoles);

  const [name, setName] = useState('');
  const [icon, setIcon] = useState(null);
  const [people, setPeople] = useState([]); // [{id, name, email, avatar_url, level}]
  // Nenhum grupo vem marcado por padrao, exceto o grupo de quem esta criando.
  const [roleIds, setRoleIds] = useState(() => new Set(user?.role_id != null ? [user.role_id] : []));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const nameRef = useRef(null);

  const peopleIds = new Set(people.map((p) => p.id));
  const { search, setSearch, results, hasMore, searching, searchError, handleShowMore, handleRetrySearch } =
    useUserSearch({ excludeIds: peopleIds });

  useEffect(() => {
    fetchRoles().catch(() => {});
    nameRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const addPerson = (u) => {
    setPeople((prev) => (prev.some((p) => p.id === u.id) ? prev : [...prev, { ...u, level: DEFAULT_GRANT_LEVEL }]));
    setSearch('');
  };

  const removePerson = (id) => setPeople((prev) => prev.filter((p) => p.id !== id));

  const changePersonLevel = (id, level) =>
    setPeople((prev) => prev.map((p) => (p.id === id ? { ...p, level } : p)));

  const toggleRole = (id) => {
    setRoleIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed) { setError('Informe um nome para a área de trabalho.'); return; }
    setSaving(true);
    setError(null);
    try {
      const members = people.map((p) => ({ user_id: p.id, level: p.level }));
      const ws = await createWorkspace(trimmed, icon, {
        members: members.length ? members : undefined,
        role_ids: roleIds.size ? [...roleIds] : undefined,
      });
      onCreated?.(ws);
    } catch (err) {
      setError(err.message || 'Erro ao criar área de trabalho');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-card rounded-xl border border-border shadow-2xl w-[560px] max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border/50 shrink-0">
          <h3 className="text-sm font-semibold text-text-primary">Nova área de trabalho</h3>
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-5">
          <div className="flex items-center gap-4">
            <Preview name={name} icon={icon} size={56} />
            <div className="flex-1">
              <label className="block text-xs text-text-secondary mb-1">Nome</label>
              <input
                ref={nameRef}
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
                placeholder="Nome da área de trabalho"
                className="w-full bg-main border border-border rounded px-3 py-2 text-sm text-text-primary outline-none focus:border-accent"
              />
            </div>
          </div>

          <div>
            <div className="text-xs text-text-secondary mb-2">Ícone</div>
            <div className="grid grid-cols-8 gap-1.5">
              <button
                onClick={() => setIcon(null)}
                className={`aspect-square rounded border flex items-center justify-center text-xs transition-colors ${
                  !icon ? 'border-accent bg-accent/10' : 'border-border hover:border-accent/40'
                }`}
                title="Letra (padrão)"
              >
                <span className="text-text-primary font-semibold">{(name || '?').trim().charAt(0).toUpperCase()}</span>
              </button>
              {WORKSPACE_ICON_NAMES.map((n) => {
                const IconComp = WORKSPACE_ICONS[n];
                return (
                  <button
                    key={n}
                    onClick={() => setIcon(n)}
                    className={`aspect-square rounded border flex items-center justify-center transition-colors ${
                      icon === n ? 'border-accent bg-accent/10' : 'border-border hover:border-accent/40'
                    }`}
                    title={n}
                  >
                    <IconComp className="w-4 h-4 text-text-primary" />
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-text-secondary/70 mt-1.5">Cor e imagem personalizada ficam disponíveis depois, em Editar área.</p>
          </div>

          <div className="pt-2 border-t border-border/50">
            <div className="text-xs text-text-secondary mb-2">Pessoas</div>
            <div className="relative">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nome ou e-mail..."
                className="w-full bg-main border border-border rounded-lg px-3 py-2 text-sm text-text-primary placeholder-text-secondary/50 outline-none focus:border-accent/50"
              />
              {search.trim() && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-main border border-border rounded-lg shadow-xl z-10 max-h-56 overflow-y-auto">
                  {results.map((u) => (
                    <button
                      key={u.id}
                      onClick={() => addPerson(u)}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-white/5 transition-colors text-left"
                    >
                      <Avatar user={u} size={7} />
                      <div className="min-w-0">
                        <div className="text-sm text-text-primary truncate">{u.name}</div>
                        <div className="text-xs text-text-secondary truncate">{u.email}</div>
                      </div>
                    </button>
                  ))}
                  {searching && results.length === 0 && (
                    <div className="px-3 py-2 text-xs text-text-secondary">Buscando...</div>
                  )}
                  {searchError && (
                    <div className="flex items-center justify-between gap-2 px-3 py-2 text-xs text-danger">
                      <span className="truncate">{searchError}</span>
                      <button onClick={handleRetrySearch} className="shrink-0 text-accent hover:underline">Tentar de novo</button>
                    </div>
                  )}
                  {!searching && !searchError && results.length === 0 && (
                    <div className="px-3 py-2 text-xs text-text-secondary">Nenhum resultado</div>
                  )}
                  {hasMore && !searchError && (
                    <button
                      onClick={handleShowMore}
                      disabled={searching}
                      className="w-full px-3 py-1.5 text-xs text-accent hover:bg-white/5 transition-colors disabled:opacity-50"
                    >
                      {searching ? 'Carregando...' : 'Mostrar mais'}
                    </button>
                  )}
                </div>
              )}
            </div>

            {people.length > 0 && (
              <div className="space-y-1 mt-2">
                {people.map((p) => (
                  <div key={p.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-white/[0.02] border border-border/50">
                    <Avatar user={p} size={6} />
                    <span className="text-sm text-text-primary truncate flex-1 min-w-0">{p.name}</span>
                    <select
                      value={p.level}
                      onChange={(e) => changePersonLevel(p.id, e.target.value)}
                      className="bg-transparent text-xs text-text-secondary border border-border/50 rounded px-1.5 py-1 outline-none focus:border-accent/50 cursor-pointer shrink-0"
                    >
                      {GRANT_LEVELS.map((l) => (
                        <option key={l.value} value={l.value}>{l.label}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => removePerson(p.id)}
                      className="p-1 rounded hover:bg-white/10 text-text-secondary hover:text-danger transition-colors shrink-0"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="pt-2 border-t border-border/50">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs text-text-secondary">Grupos com acesso a esta área</div>
              <Link
                to="/settings/users"
                className="flex items-center gap-1 text-xs text-accent hover:underline shrink-0"
              >
                Ajustar permissões <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            {loadingRoles && roles.length === 0 && (
              <p className="text-xs text-text-secondary">Carregando grupos...</p>
            )}
            <div className="space-y-1">
              {roles.map((r) => {
                const checked = roleIds.has(r.id);
                return (
                  <button
                    key={r.id}
                    onClick={() => toggleRole(r.id)}
                    className="flex items-center gap-2 w-full text-left px-2 py-1.5 rounded hover:bg-white/5 transition-colors"
                  >
                    <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${checked ? 'bg-accent border-accent' : 'border-border'}`}>
                      {checked && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <span className="text-xs text-text-primary truncate">{r.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {error && <p className="text-xs text-danger">{error}</p>}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-border/50 shrink-0">
          <button onClick={onClose} className="px-4 py-1.5 text-sm text-text-secondary hover:text-text-primary transition-colors">
            Cancelar
          </button>
          <button
            onClick={handleCreate}
            disabled={saving}
            className="px-4 py-1.5 bg-accent text-white text-sm rounded hover:bg-accent/80 transition-colors disabled:opacity-50"
          >
            {saving ? 'Criando...' : 'Criar área de trabalho'}
          </button>
        </div>
      </div>
    </div>
  );
}
